// Iter 46 — Intra-Kiosk Chat Engine
// Premium 3-pane chat UI: glassmorphic station sidebar (with neon-green
// pulsing online dots), elegant smooth-scrolling message stream with
// distinct bubble colors (premium blue for self / slate gray for other
// stores), and a polished action bar with quick-reply pills + SOS siren.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useSession } from "@/lib/session";
import { toast } from "sonner";
import { Send, Siren, Radio, Wifi, WifiOff, Package, ArrowRightLeft } from "lucide-react";

const QUICK_REPLIES = [
  "👍 Έγινε!",
  "🚚 Έρχεται ο οδηγός",
  "🚬 Στείλαμε Heets",
  "✅ Όλα καλά εδώ",
];

// Synthesize an alarm chime via the Web Audio API so we don't ship a
// binary asset. Three rapid descending tones — distinct from any other
// kiosk sound. Browsers gate audio behind a user gesture, so we boot
// the AudioContext lazily on the first SOS receipt.
let _audioCtx = null;
function playSosChime() {
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    const now = ctx.currentTime;
    [880, 660, 440].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = freq;
      gain.gain.value = 0.15;
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.2);
      osc.stop(now + i * 0.2 + 0.18);
    });
  } catch {
    /* AudioContext unavailable — silent fallback. */
  }
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch {
    return "";
  }
}

export default function Chat() {
  const { shift } = useSession();
  const [stations, setStations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [wsState, setWsState] = useState("connecting"); // connecting|open|closed
  const [sosFlash, setSosFlash] = useState(null);       // {station, body, at}
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  const selfStation = (shift?.location || "").trim();

  // ── Initial load: stations + history
  useEffect(() => {
    api.get("/chat/stations")
      .then(({ data }) => setStations(data.stations || []))
      .catch(() => setStations([]));
    api.get("/chat/history?limit=80")
      .then(({ data }) => setMessages(data.messages || []))
      .catch(() => setMessages([]));
    // Refresh stations every 15s so online/offline dots stay live
    const t = setInterval(() => {
      api.get("/chat/stations")
        .then(({ data }) => setStations(data.stations || []))
        .catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, []);

  // ── WebSocket delivery channel
  useEffect(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    const jwt = localStorage.getItem("kf_jwt") || "";
    // Master kiosk fallback: AppLock-header style query param. The backend
    // accepts either; one will always be present on a locked kiosk.
    const url = jwt
      ? `${base}/api/ws/chat?auth=${encodeURIComponent(jwt)}`
      : `${base}/api/ws/chat?applock=savvaspos:store`;

    let alive = true;
    let backoff = 1000;
    function connect() {
      if (!alive) return;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      setWsState("connecting");
      ws.onopen = () => {
        setWsState("open");
        backoff = 1000;
      };
      ws.onmessage = (ev) => {
        try {
          const frame = JSON.parse(ev.data);
          if (frame.type === "msg" || frame.type === "sos") {
            setMessages((prev) => [...prev, frame.message]);
            if (frame.type === "sos") {
              playSosChime();
              setSosFlash({
                station: frame.message.station,
                body: frame.message.body,
                at: frame.message.created_at,
              });
              setTimeout(() => setSosFlash(null), 8000);
            }
          }
        } catch { /* ignore malformed frames */ }
      };
      ws.onclose = () => {
        setWsState("closed");
        if (alive) {
          setTimeout(connect, backoff);
          backoff = Math.min(backoff * 2, 15000);
        }
      };
      ws.onerror = () => {
        try { ws.close(); } catch { /* noop */ }
      };
    }
    connect();
    return () => {
      alive = false;
      try { wsRef.current && wsRef.current.close(); } catch { /* noop */ }
    };
  }, []);

  // ── Auto-scroll on new message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages.length]);

  const onlineCount = useMemo(
    () => stations.filter((s) => s.online).length,
    [stations],
  );

  async function sendMessage(body, kind = "msg") {
    const text = (body ?? input).trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await api.post("/chat/send", {
        body: text,
        station: selfStation || "Main Terminal",
        register: shift?.register || "",
        kind,
      });
      if (!body) setInput("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  }

  async function sendSos() {
    if (!window.confirm("🚨 Send EMERGENCY SOS to every kiosk in the network?")) return;
    const station = selfStation || "Main Terminal";
    await sendMessage(`⚠️ SOS · ${station} requires immediate attention!`, "sos");
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  // Iter 49 — Inventory transfer widget embedded in chat layout
  const STATIONS = ["Vizantiou", "Stavrou", "Kallipoleos", "Aglantzia"];
  const [invItem, setInvItem] = useState("");
  const [invQty, setInvQty] = useState("");
  const [invFrom, setInvFrom] = useState(selfStation || "Vizantiou");
  const [invTo, setInvTo] = useState("Stavrou");
  const [invBusy, setInvBusy] = useState(false);
  const [invInventory, setInvInventory] = useState({});
  const loadInventory = async () => {
    try {
      const { data } = await api.get("/inventory/items");
      setInvInventory(data.by_station || {});
    } catch { /* tolerate */ }
  };
  useEffect(() => { loadInventory(); }, []);
  const submitTransfer = async () => {
    if (!invItem.trim() || !invQty || invFrom === invTo) {
      toast.error("Fill item, qty, and pick different from/to stations");
      return;
    }
    setInvBusy(true);
    try {
      const { data } = await api.post("/inventory/transfer", {
        item_name: invItem.trim(),
        qty: Number(invQty),
        from_station: invFrom,
        to_station: invTo,
      });
      toast.success(`Transferred ${data.qty} × ${data.item_name} : ${data.from.station}(${data.from.after}) → ${data.to.station}(${data.to.after})`);
      setInvItem(""); setInvQty("");
      loadInventory();
      // also broadcast to chat
      sendMessage(`📦 STOCK MOVE · ${data.qty} × ${data.item_name} → ${invTo} (now ${data.to.after})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfer failed");
    } finally {
      setInvBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Iter 49 — Inventory Transfer widget */}
      <section
        className="rounded-2xl border-2 border-amber-300/60 bg-gradient-to-br from-amber-50 to-orange-50 shadow-sm p-4"
        data-testid="inventory-transfer-card"
      >
        <div className="flex items-center gap-2 mb-3">
          <Package className="h-5 w-5 text-amber-700" />
          <h2 className="font-black tracking-tight text-amber-900">
            📦 ΜΕΤΑΦΟΡΑ ΣΤΟΚ / INVENTORY TRANSFER
          </h2>
        </div>
        <div className="grid sm:grid-cols-[2fr_1fr_1.2fr_auto_1.2fr_auto] gap-2 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-amber-800">Item</label>
            <input
              value={invItem}
              onChange={(e) => setInvItem(e.target.value)}
              placeholder="Heets Amber"
              data-testid="inventory-item-input"
              className="w-full mt-1 border-2 border-amber-300 rounded px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-amber-800">Qty</label>
            <input
              type="number" min="1" step="1"
              value={invQty}
              onChange={(e) => setInvQty(e.target.value)}
              placeholder="15"
              data-testid="inventory-qty-input"
              className="w-full mt-1 border-2 border-amber-300 rounded px-2 py-1.5 text-sm tabular-nums"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-amber-800">From</label>
            <select
              value={invFrom}
              onChange={(e) => setInvFrom(e.target.value)}
              data-testid="inventory-from-select"
              className="w-full mt-1 border-2 border-amber-300 rounded px-2 py-1.5 text-sm bg-white"
            >
              {STATIONS.map((s) => (
                <option key={s} value={s}>
                  {s} {invInventory[s]?.find((x) => x.item_name === invItem.trim())?.qty != null
                    ? `(${invInventory[s].find((x) => x.item_name === invItem.trim()).qty})`
                    : ""}
                </option>
              ))}
            </select>
          </div>
          <ArrowRightLeft className="h-5 w-5 text-amber-700 mb-2.5 mx-auto" />
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-amber-800">To</label>
            <select
              value={invTo}
              onChange={(e) => setInvTo(e.target.value)}
              data-testid="inventory-to-select"
              className="w-full mt-1 border-2 border-amber-300 rounded px-2 py-1.5 text-sm bg-white"
            >
              {STATIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <button
            onClick={submitTransfer}
            disabled={invBusy}
            data-testid="inventory-transfer-btn"
            className="h-[38px] mt-auto bg-amber-600 hover:bg-amber-700 text-white font-bold px-4 rounded text-sm disabled:opacity-50"
          >
            Transfer
          </button>
        </div>
      </section>

    <div className="relative flex flex-col lg:flex-row gap-4 h-[calc(100vh-14rem)]" data-testid="chat-root">
      {/* SOS overlay — full-viewport crimson flash, sound triggered separately */}
      {sosFlash && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none animate-pulse"
          style={{ background: "rgba(220,38,38,0.85)" }}
          data-testid="sos-overlay"
        >
          <div className="bg-white/95 rounded-2xl shadow-2xl p-8 max-w-xl text-center border-4 border-red-700">
            <div className="text-6xl mb-2">🚨</div>
            <div className="text-3xl font-black text-red-700 tracking-tight mb-2">
              SOS · ΣΥΝΑΓΕΡΜΟΣ
            </div>
            <div className="text-lg text-slate-900 font-semibold">
              EMERGENCY SECURITY ALERT from <span className="text-red-700">{sosFlash.station}</span>
            </div>
            <div className="mt-3 text-sm text-slate-700">{sosFlash.body}</div>
            <div className="mt-4 text-xs text-slate-500 uppercase tracking-wider">
              Check the chat feed immediately
            </div>
          </div>
        </div>
      )}

      {/* ── Left sidebar: stations with neon online dots ── */}
      <aside
        className="lg:w-72 w-full lg:flex-shrink-0 rounded-2xl border border-slate-200/70 bg-white/60 backdrop-blur-xl shadow-lg overflow-hidden flex flex-col"
        data-testid="chat-stations"
      >
        <div className="px-4 py-3 border-b border-slate-200/70 bg-gradient-to-br from-slate-900 to-slate-700 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Radio className="h-5 w-5" />
              <span className="font-bold tracking-tight">Network</span>
            </div>
            <div
              className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${
                wsState === "open" ? "bg-emerald-500/20 text-emerald-200" : "bg-amber-500/20 text-amber-200"
              }`}
              data-testid="chat-ws-status"
            >
              {wsState === "open" ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
              {wsState}
            </div>
          </div>
          <div className="text-xs text-slate-300 mt-1">
            {onlineCount} of {stations.length} terminals online
          </div>
        </div>
        <ul className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {stations.map((s) => {
            const isSelf = selfStation && s.name === selfStation;
            return (
              <li
                key={s.name}
                className={`px-4 py-3 flex items-center justify-between hover:bg-slate-50/80 transition-colors ${
                  isSelf ? "bg-blue-50/60" : ""
                }`}
                data-testid={`chat-station-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center gap-3">
                  <span className="relative inline-flex h-3 w-3">
                    {s.online && (
                      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    )}
                    <span
                      className={`relative inline-flex rounded-full h-3 w-3 ${
                        s.online ? "bg-emerald-500 shadow-lg shadow-emerald-500/50" : "bg-slate-300"
                      }`}
                    />
                  </span>
                  <div className="leading-tight">
                    <div className="font-semibold text-slate-900 text-sm">
                      {s.name} {isSelf && <span className="text-blue-600 text-[10px]">(you)</span>}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {s.online
                        ? `${s.employee || "On shift"} · R${s.register || "—"}`
                        : "Offline"}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
          {stations.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-slate-500">
              No stations registered yet.
            </li>
          )}
        </ul>
      </aside>

      {/* ── Main message column ── */}
      <section className="flex-1 flex flex-col rounded-2xl border border-slate-200/70 bg-white shadow-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-white flex items-center justify-between">
          <div>
            <div className="font-black tracking-tight text-slate-900">💬 Intra-Kiosk Chat</div>
            <div className="text-xs text-slate-500">
              Tenant-isolated · live broadcast to every terminal in your network
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50/40" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-center text-sm text-slate-400 py-12">
              No messages yet — be the first to ping the network.
            </div>
          )}
          {messages.map((m) => {
            const isSelf = selfStation && m.station === selfStation;
            const isSos = m.kind === "sos";
            return (
              <div
                key={m.id}
                className={`flex ${isSelf ? "justify-end" : "justify-start"}`}
                data-testid={`chat-msg-${m.id}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 shadow-sm ${
                    isSos
                      ? "bg-red-600 text-white border border-red-700 animate-pulse"
                      : isSelf
                      ? "bg-blue-600 text-white"
                      : "bg-slate-700 text-white"
                  }`}
                >
                  <div className={`text-[10px] uppercase tracking-wider mb-0.5 ${
                    isSos ? "text-red-100" : isSelf ? "text-blue-100" : "text-slate-300"
                  }`}>
                    [{fmtTime(m.created_at)}] {m.station}
                    {m.employee && ` · ${m.employee.split("@")[0]}`}
                    {isSos && " · 🚨 SOS"}
                  </div>
                  <div className="text-sm leading-snug whitespace-pre-wrap break-words">
                    {m.body}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* ── Action bar ── */}
        <div className="border-t border-slate-200 bg-white px-4 py-3 space-y-2">
          <div className="flex flex-wrap gap-2">
            {QUICK_REPLIES.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => sendMessage(q)}
                disabled={sending}
                className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-colors disabled:opacity-50"
                data-testid={`chat-quick-${q.slice(0, 10)}`}
              >
                {q}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type a message · press Enter to send"
              className="flex-1 px-4 py-3 rounded-xl border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm"
              disabled={sending}
              data-testid="chat-input"
            />
            <button
              type="button"
              onClick={sendSos}
              disabled={sending}
              className="h-12 w-12 flex items-center justify-center rounded-xl bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-red-300/50 transition-all disabled:opacity-50"
              title="EMERGENCY SOS"
              data-testid="chat-sos-btn"
            >
              <Siren className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={() => sendMessage()}
              disabled={sending || !input.trim()}
              className="h-12 px-5 flex items-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg disabled:opacity-50 transition-colors"
              data-testid="chat-send-btn"
            >
              <Send className="h-4 w-4" />
              <span className="hidden sm:inline">Send</span>
            </button>
          </div>
        </div>
      </section>
    </div>
    </div>
  );
}
